import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductTabsRefs
 * @property {HTMLElement[]} tab
 * @property {HTMLElement[]} panel
 */

/** @extends {Component<ProductTabsRefs>} */
export class ProductTabsComponent extends Component {
  requiredRefs = ['tab', 'panel'];
  #activeIndex = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tab, panel } = this.refs;
    
    if (!tab?.length || !panel?.length) return;
    
    // Find initial active tab
    const activeTab = tab.find((t) => t.classList.contains('frd-product-tabs__tab--active'));
    if (activeTab) {
      this.#activeIndex = tab.indexOf(activeTab);
    }
    
    this.#setupEventListeners();
    this.#updateActiveTab();
    
    // Parse Rich Text for initially visible panel
    if (panel[this.#activeIndex]) {
      this.#parseRichText(panel[this.#activeIndex]);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tab } = this.refs;

    // Handle tab clicks
    for (const [i, tabButton] of tab.entries()) {
      tabButton.addEventListener('click', () => this.#selectTab(i), opts);
      tabButton.addEventListener('keydown', (e) => this.#handleKeydown(e, i), opts);
    }
  }

  #handleKeydown(event, index) {
    const { tab } = this.refs;
    if (!tab) return;

    let targetIndex = index;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        targetIndex = index > 0 ? index - 1 : tab.length - 1;
        this.#selectTab(targetIndex);
        tab[targetIndex]?.focus();
        break;
      case 'ArrowRight':
        event.preventDefault();
        targetIndex = index < tab.length - 1 ? index + 1 : 0;
        this.#selectTab(targetIndex);
        tab[targetIndex]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        this.#selectTab(0);
        tab[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        this.#selectTab(tab.length - 1);
        tab[tab.length - 1]?.focus();
        break;
    }
  }

  #selectTab(index) {
    if (index === this.#activeIndex) return;
    
    this.#activeIndex = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { tab, panel } = this.refs;
    if (!tab || !panel) return;

    for (const [i, tabButton] of tab.entries()) {
      const isActive = i === this.#activeIndex;
      tabButton.classList.toggle('frd-product-tabs__tab--active', isActive);
      tabButton.setAttribute('aria-selected', String(isActive));
      tabButton.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panelElement] of panel.entries()) {
      const isActive = i === this.#activeIndex;
      panelElement.classList.toggle('frd-product-tabs__panel--active', isActive);
      if (isActive) {
        panelElement.removeAttribute('hidden');
        // Parse Rich Text JSON when panel becomes active
        this.#parseRichText(panelElement);
      } else {
        panelElement.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Parses Rich Text JSON structure and converts it to HTML
   * @param {HTMLElement} panelElement - The panel element containing rich text
   */
  #parseRichText(panelElement) {
    const richTextElements = panelElement.querySelectorAll('[data-rich-text-json]');
    
    for (const element of richTextElements) {
      const jsonString = element.getAttribute('data-rich-text-json');
      if (!jsonString) continue;

      try {
        // Convert Ruby hash syntax to JSON
        // Handle both "key"=>"value" and "key":"value" formats
        let jsonStringFixed = jsonString
          .replace(/"([^"]+)"\s*=>\s*"/g, '"$1": "')  // "key"=>"value" to "key": "value"
          .replace(/"([^"]+)"\s*=>\s*\[/g, '"$1": [')  // "key"=>[ to "key": [
          .replace(/"([^"]+)"\s*=>\s*\{/g, '"$1": {')  // "key"=>{ to "key": {
          .replace(/'/g, '"');  // Single quotes to double quotes
        
        const data = JSON.parse(jsonStringFixed);
        const html = this.#convertRichTextToHTML(data);
        
        if (html) {
          element.innerHTML = html;
          element.removeAttribute('data-rich-text-json');
        }
      } catch (e) {
        console.warn('Failed to parse rich text JSON:', e);
        // Fallback: try to extract text directly
        const textMatch = jsonString.match(/"value"=>"([^"]*(?:\\.[^"]*)*)"/);
        if (textMatch && textMatch[1]) {
          const text = textMatch[1].replace(/\\n/g, '\n');
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length > 0) {
            element.innerHTML = '<ul>' + lines.map(line => `<li>${this.#escapeHtml(line.trim())}</li>`).join('') + '</ul>';
            element.removeAttribute('data-rich-text-json');
          }
        }
      }
    }
  }

  /**
   * Converts Rich Text JSON structure to HTML
   * @param {Object} node - The Rich Text node
   * @returns {string} HTML string
   */
  #convertRichTextToHTML(node) {
    if (!node || !node.children) return '';

    let html = '';

    for (const child of node.children) {
      switch (child.type) {
        case 'paragraph':
          html += '<p>';
          html += this.#processChildren(child);
          html += '</p>';
          break;
        case 'list':
          const listTag = child.listType === 'ordered' ? 'ol' : 'ul';
          html += `<${listTag}>`;
          html += this.#processChildren(child);
          html += `</${listTag}>`;
          break;
        case 'list-item':
          html += '<li>';
          html += this.#processChildren(child);
          html += '</li>';
          break;
        case 'text':
          if (child.value) {
            // Split by newlines and create list items if multiple lines
            const lines = child.value.split('\n').filter(line => line.trim());
            if (lines.length > 1) {
              html += '<ul>';
              for (const line of lines) {
                html += `<li>${this.#escapeHtml(line.trim())}</li>`;
              }
              html += '</ul>';
            } else {
              html += this.#escapeHtml(child.value);
            }
          }
          break;
        default:
          html += this.#processChildren(child);
      }
    }

    return html;
  }

  /**
   * Processes children nodes recursively
   * @param {Object} node - The node to process
   * @returns {string} HTML string
   */
  #processChildren(node) {
    if (!node.children) return '';
    
    let html = '';
    for (const child of node.children) {
      html += this.#convertRichTextToHTML(child);
    }
    return html;
  }

  /**
   * Escapes HTML special characters
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('product-tabs-component', ProductTabsComponent);
